import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useCohortDefinitionStore } from '@verantos-dev/app-state-cohort-definition';
import { useAuth } from '@verantos-dev/core-auth';
import { cohortQueryKeys, useCohortUsageQuery } from '@verantos-dev/data-access-cohorts';
import type { CohortProfileQueryData } from '@verantos-dev/feature-analytical-variables';
import { useCohortProfileQuery } from '@verantos-dev/feature-analytical-variables';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

interface CohortProfileDataParams {
  projectId: string;
  cohortId: string;
  cohortVersionId: string;
  enabled?: boolean;
  shouldPoll?: boolean;
}

/**
 * Orchestrates TanStack Query → Zustand sync for cohort profile data.
 * Fetches profile (status, latest version, creator) and hydrates the
 * Zustand profile state. Computes `isEditable` from the creator userId
 * vs the authenticated user.
 *
 * Same ref-based dedup pattern as `useCohortDefinitionData` — only
 * hydrates when TQ returns new data.
 */
export const useCohortProfileData = (params: CohortProfileDataParams) => {
  const { projectId, cohortId, cohortVersionId, enabled, shouldPoll } = params;
  const { data, isLoading, error } = useCohortProfileQuery(
    { projectId, cohortId, cohortVersionId },
    { enabled, shouldPoll }
  );

  const {
    user: { auth0Id, userId },
  } = useAuth();

  const queryClient = useQueryClient();
  const setProfileFromServer = useCohortDefinitionStore((s) => s.setProfileFromServer);
  const setUsedInAnalyticalDatasets = useCohortDefinitionStore(
    (s) => s.setUsedInAnalyticalDatasets
  );
  const setAssetEditWhileInUseEnabled = useCohortDefinitionStore(
    (s) => s.setAssetEditWhileInUseEnabled
  );
  const setSkipProcessConfirmationModalEnabled = useCohortDefinitionStore(
    (s) => s.setSkipProcessConfirmationModalEnabled
  );

  // Track which analytical datasets reference this cohort. When
  // `asset_edit_while_in_use` is off this locks the cohort read-only (folded
  // into `selectIsEditable`, with a banner naming the datasets); when on it
  // only blocks reprocessing while a dependent AD is PROCESSING.
  const { data: usage } = useCohortUsageQuery({
    projectId,
    cohortId,
    enabled: enabled !== false,
  });
  const usageDatasets = usage?.analyticalDatasets;

  // TODO: cleanup with asset_edit_while_in_use ff — hydrate the flag into the
  // store so the pure `selectIsEditable` can decide whether AD usage locks the
  // cohort read-only (flag off) or leaves it editable (flag on).
  const isAssetEditWhileInUseEnabled = Boolean(useFeatureFlagSplit('asset_edit_while_in_use'));

  // TODO: cleanup with skip_process_confirmation_modal ff
  const isSkipProcessConfirmationModalEnabled = Boolean(
    useFeatureFlagSplit('skip_process_confirmation_modal')
  );

  useEffect(() => {
    setUsedInAnalyticalDatasets(usageDatasets ?? []);
  }, [usageDatasets, setUsedInAnalyticalDatasets]);

  useEffect(() => {
    setAssetEditWhileInUseEnabled(isAssetEditWhileInUseEnabled);
  }, [isAssetEditWhileInUseEnabled, setAssetEditWhileInUseEnabled]);

  useEffect(() => {
    setSkipProcessConfirmationModalEnabled(isSkipProcessConfirmationModalEnabled);
  }, [isSkipProcessConfirmationModalEnabled, setSkipProcessConfirmationModalEnabled]);

  // Track last processed data to prevent redundant hydration
  const lastProcessedDataRef = useRef<CohortProfileQueryData | undefined>();

  useEffect(() => {
    if (!data || data === lastProcessedDataRef.current) {
      return;
    }

    lastProcessedDataRef.current = data;

    // Compute isEditable — same logic as legacy usePrefetchCohortData
    const isEditable = [auth0Id, userId].includes(data.createdByUserId);

    setProfileFromServer({
      latestStatus: data.latestStatus,
      latestCohortVersionId: data.latestCohortVersionId,
      isEditable,
      latestSubjectCount: data.latestSubjectCount,
      latestDatasetReleaseId: data.latestDatasetReleaseId,
      latestDatasetReleaseDisplayName: data.latestDatasetReleaseDisplayName,
      // Preserve current isSaving state — profile fetch shouldn't reset it
      isSaving: useCohortDefinitionStore.getState().profile.isSaving,
      // Preserve current execution gate — profile fetch shouldn't reset it
      isExecutionPending: useCohortDefinitionStore.getState().profile.isExecutionPending,
    });
  }, [data, auth0Id, userId, setProfileFromServer]);

  // When the AV polling detects that processing has completed, bust the
  // cohort-id-keyed profile cache in data-access-cohorts. Without this,
  // that cache stays stranded at PROCESSING (last set by useProcessCohortMutation
  // onSuccess invalidation) and the delete modal shows "still processing" until
  // the next window-focus refetch.
  const prevStatusRef = useRef<string | undefined>();
  const latestStatus = data?.latestStatus;
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = latestStatus;

    if (
      prevStatus === CohortDefinitionStatus.PROCESSING &&
      latestStatus !== CohortDefinitionStatus.PROCESSING
    ) {
      queryClient.invalidateQueries({ queryKey: cohortQueryKeys.profile(cohortId) });
    }
  }, [latestStatus, cohortId, queryClient]);

  return { isLoading, error };
};
